import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { initializeRealUserMonitoring, setupGlobalErrorHandler } from '@/lib/error-monitoring';
import { initializeAnalytics } from '@/lib/analytics';
import { registerServiceWorker } from '@/lib/pwa';
import { I18nProvider } from '@/lib/i18n';

import App from './App.tsx';
import { ErrorFallback } from './ErrorFallback.tsx';

import './main.css';

// Initialize monitoring systems
setupGlobalErrorHandler();
initializeRealUserMonitoring();
initializeAnalytics();

// Keep the optional Firebase Performance SDK out of the critical render path.
// The RUM collector above still captures the initial page metrics locally.
const scheduleFirebasePerformance = () => {
  const requestIdle = (window as Window & { requestIdleCallback?: typeof requestIdleCallback }).requestIdleCallback;
  const loadPerformance = () => {
    void import('@/lib/firebase').then(({ initializeFirebasePerformance }) => initializeFirebasePerformance());
  };
  if (requestIdle) {
    requestIdle(loadPerformance, { timeout: 4000 });
  } else {
    window.setTimeout(loadPerformance, 2500);
  }
};

if (document.readyState === 'complete') {
  scheduleFirebasePerformance();
} else {
  window.addEventListener('load', scheduleFirebasePerformance, { once: true });
}
registerServiceWorker();

createRoot(document.getElementById('root')!).render(
  <I18nProvider>
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <App />
    </ErrorBoundary>
  </I18nProvider>,
);
